// Conversational Forms provider — the feature's single mount point.
//
// Everything here is gated on `form !== null` (config carries conversational
// metadata). When the gate is closed the provider renders its children and does
// nothing else: no surfaces, no timers, no network calls (spec: "No config, no
// feature").

import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import api from '../../API/api';
import { EdullaConfig } from '../../config/configType';
import { AppContext } from '../App/AppContext';
import { ConfigContext } from '../App/App';
import { ChatMessage } from '../Shared/API/ApiTypes';
import {
    MIN_THINKING_MS,
    SLOW_TURN_MS,
    TURN_ERROR_MESSAGE,
    TURN_TIMEOUT_MESSAGE,
    TURN_TIMEOUT_MS,
    DISCARD_ACKNOWLEDGEMENT,
    SUBMITTED_ACKNOWLEDGEMENT,
    discardWithQueueAcknowledgement,
    submittedNextUpAcknowledgement,
    switchAcknowledgement,
    updateAcknowledgement,
} from './constants';
import {
    HELP_FILL_FALLBACK_AUTO,
    HELP_FILL_FALLBACK_SUGGESTED,
    HelpFill,
    HelpMessage,
} from './FieldHelpThread';
import { logFormStarted } from './formsAnalytics';
import { CfRegistry, parseFormsRegistry } from './formsConfig';
import {
    acceptsAutoFill,
    CfAction,
    CfState,
    formsReducer,
    getFormsStorageIdentifier,
    initialFormsState,
} from './formsReducer';
import { useFormsPersistence } from './useFormsPersistence';
import { allFieldIds, nextRequired, openForAI, progressPct, statusOf, suggestedCount } from './formsSelectors';
import { buildFormsTheme, FormsTheme } from './theming';
import { CfField, CfForm, FieldValue } from './types';
import { classifyTurn, committableFills, fillsRetryable, hydrateFieldValue, normalizeFills, toOpenFieldSpecs, toReceipts, toSaveFields } from './turnHelpers';
import { sameValue } from './valueFormat';

type FormsContextValue = {
    /** The feature is configured and may render. */
    active: boolean;
    /**
     * The form the surfaces present: the active form when one is pinned, else
     * the tenant's config form. Single-form tenants see exactly the old value.
     */
    form: CfForm | null;
    /** Every form this widget holds a definition for (config form + BotForms). */
    registry: CfRegistry | null;
    state: CfState;
    dispatch: React.Dispatch<CfAction>;
    theme: FormsTheme;
    pct: number;
    pendingCount: number;
    nextRequiredField: CfField | undefined;
    /** Run a conversational turn for `text`. Queued FIFO behind any in-flight turn. */
    sendTurn: (text: string) => Promise<void>;
    /** Re-run a failed turn's text, removing its error bubble at `index`. */
    retryTurn: (index: number, text: string) => void;
    /** Start `formId` from its offer card (the FLIP rect floats it to the pin). */
    startForm: (formId: string, rect?: DOMRect | null) => void;
    /**
     * Activate a registry form, pausing any active one to the front of the
     * queue — values preserved, transition acknowledged in chat (design D2/D5).
     */
    switchToForm: (formId: string) => void;
    /**
     * Engagement closure (store tracker 6.55): pause the active form —
     * unpin it to the front of the queue, touch no data, post no
     * acknowledgement. The pinned card's ✕; distinct from `discardForm`,
     * which lives only in the form view.
     */
    pauseForm: () => void;
    /** Quietly drop a queued form: no dismiss call, a later trigger may re-offer. */
    removeQueuedForm: (formId: string) => void;
    confirmSuggestion: (fieldId: string) => void;
    confirmAllSuggestions: () => void;
    undoSuggestion: (fieldId: string) => void;
    requestDiscard: () => void;
    keepForm: () => void;
    discardForm: () => void;
    /** The form-view overlay is requested open. The mount point still gates on a
     *  pinned form, so a discard underneath drops the overlay with it. */
    formViewOpen: boolean;
    openFormView: () => void;
    closeFormView: () => void;
    /** Post the full field list. Resolves false on failure — values stay local. */
    submitForm: () => Promise<boolean>;
    /** After a successful submit's Done: unpin the card and acknowledge in chat. */
    completeSubmission: () => void;
    /**
     * Forms-corrections slice (D1): the submitted form currently open for
     * review, independent of `activeFormId` — reviewing a submitted form
     * never pins it or disturbs whatever form (if any) is actively in
     * progress. Null when the overlay is not in review mode.
     */
    reviewFormId: string | null;
    /** Open `formId` (a submitted form) in the overlay's review variant. */
    openReview: (formId: string) => void;
    /** The re-post for a correction (D3): same `persist` as first submit, but
     *  targets `formId` explicitly rather than the pinned/active form —
     *  review can happen while a different form is active. */
    updateSubmission: (formId: string) => Promise<boolean>;
    /** After a successful update: clear dirty and acknowledge lightly in chat.
     *  Deliberately does NOT call `completeSubmission` — no queue handoff, no
     *  second `markSubmitted` (the form was already submitted). */
    completeUpdate: (formId: string) => void;
    /** Rect of the start card at the moment Start was tapped, for the FLIP float. */
    flipRectRef: React.MutableRefObject<DOMRect | null>;

    /* ---------------- Field help (slice 5) ----------------
       Session-local UI state, the `reviewFormId` precedent: React state here,
       never the persisted/normalized forms state (design.md D3 — the field's
       value is the durable outcome, not the conversation). */
    /** Which field's help thread is open, or null. At most one open at a time —
     *  the sheet is a single overlay instance; the inline host reads this to
     *  know which row's panel to expand. */
    helpFieldId: string | null;
    /** Per-field thread history, accumulating for the session (design D3). */
    helpThreads: Record<string, HelpMessage[]>;
    /** Open `fieldId`'s help thread. */
    openHelp: (fieldId: string) => void;
    /** Close whichever thread is open; history is untouched (design D3). */
    closeHelp: () => void;
    /** Send `text` in `fieldId`'s thread: fill-first extraction against that one
     *  field, mode-aware commit + in-thread receipt, or the explanation reply.
     *  Returns `false` (send-time guard refused it, nothing appended/sent) or
     *  `true` (accepted) SYNCHRONOUSLY (adversarial-review N1) — the turn
     *  itself runs detached, so the composer's draft-clear never waits on it. */
    sendHelp: (fieldId: string, text: string) => boolean;
    /** Remove the error bubble at `index` in `fieldId`'s thread and resend. */
    retryHelp: (fieldId: string, index: number, text: string) => void;
    /** Fields with a help turn in flight. */
    helpBusyFieldIds: ReadonlySet<string>;
    /** Of those, the ones past the slow-turn threshold. */
    helpSlowFieldIds: ReadonlySet<string>;
};

const noop = () => undefined;

const defaultValue: FormsContextValue = {
    active: false,
    form: null,
    registry: null,
    state: initialFormsState,
    dispatch: noop,
    theme: buildFormsTheme({} as EdullaConfig),
    pct: 0,
    pendingCount: 0,
    nextRequiredField: undefined,
    sendTurn: async () => undefined,
    retryTurn: noop,
    startForm: noop,
    switchToForm: noop,
    pauseForm: noop,
    removeQueuedForm: noop,
    confirmSuggestion: noop,
    confirmAllSuggestions: noop,
    undoSuggestion: noop,
    requestDiscard: noop,
    keepForm: noop,
    discardForm: noop,
    formViewOpen: false,
    openFormView: noop,
    closeFormView: noop,
    submitForm: async () => false,
    completeSubmission: noop,
    reviewFormId: null,
    openReview: noop,
    updateSubmission: async () => false,
    completeUpdate: noop,
    flipRectRef: { current: null },
    helpFieldId: null,
    helpThreads: {},
    openHelp: noop,
    closeHelp: noop,
    sendHelp: () => false,
    retryHelp: noop,
    helpBusyFieldIds: new Set(),
    helpSlowFieldIds: new Set(),
};

export const FormsContext = createContext<FormsContextValue>(defaultValue);

export const useConversationalForms = () => useContext(FormsContext);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Immutable add/remove for the field-help busy/slow marker sets — small
 *  enough not to warrant a reducer action of their own (session-local UI
 *  state, not form state). */
const withId = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    if (set.has(id)) return set;
    const next = new Set(set);
    next.add(id);
    return next;
};
const withoutId = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    if (!set.has(id)) return set;
    const next = new Set(set);
    next.delete(id);
    return next;
};

/**
 * Every receipt a help turn's commit produced, as `HelpFill`s (adversarial-
 * review M7 — a turn can fill more than the scoped field, and none of them
 * are ever dropped silently: auto mode already persisted them all
 * server-side). `scopedFieldId` marks which one is this thread's own field,
 * rendered as the primary line; the rest render as compact secondary ones.
 */
const toHelpFills = (
    receipts: ReturnType<typeof toReceipts> | undefined,
    scopedFieldId: string
): HelpFill[] | undefined =>
    receipts?.length
        ? receipts.map((r) => ({
              fieldId: r.fieldId,
              label: r.label,
              display: r.display,
              sensitive: r.sensitive === true,
              scoped: r.fieldId === scopedFieldId,
          }))
        : undefined;

export const FormsProvider = ({ children }: { children: ReactNode }) => {
    const config = useContext(ConfigContext);
    const { state: appState, dispatch: appDispatch } = useContext(AppContext);

    const registry = useMemo(() => parseFormsRegistry(config), [config]);
    const active = registry !== null;

    const [state, persistDispatch] = useFormsPersistence(getFormsStorageIdentifier());

    // The presentation form: the active form when one is pinned (multiform),
    // else the config form — which for a single-form tenant is the same value
    // the provider always exposed.
    const form = useMemo<CfForm | null>(() => {
        if (!registry) return null;
        // An active id the registry no longer carries (a form unpublished
        // mid-session) presents nothing rather than the wrong form.
        if (state.activeFormId) return registry.byId.get(state.activeFormId) ?? null;
        return registry.primary;
    }, [registry, state.activeFormId]);

    const theme = useMemo(() => buildFormsTheme(config), [config]);
    // Overlay-open is UI state, not form state: it never persists, and the
    // overlay's mount point additionally gates on a pinned form.
    const [formViewOpen, setFormViewOpen] = useState(false);
    const openFormView = useCallback(() => setFormViewOpen(true), []);
    // Forms-corrections slice (D1): which submitted form review is showing,
    // decoupled from `activeFormId` — reviewing never pins the form or
    // disturbs the queue. Cleared on any overlay close, clean or dirty; the
    // dirty flag itself lives in `state.dirtyFormIds` and outlives the close.
    const [reviewFormId, setReviewFormId] = useState<string | null>(null);
    // Field help (slice 5): which field's thread is open, and per-field thread
    // history for the session. Same posture as `reviewFormId` — plain React
    // state, never the persisted/normalized forms state, so a reload starts
    // with every thread closed and empty (design D3). Closing the overlay
    // (below) closes whichever thread is open without discarding its history —
    // "closing the sheet keeps the thread" holds one level up too.
    const [helpFieldId, setHelpFieldId] = useState<string | null>(null);
    const [helpThreads, setHelpThreads] = useState<Record<string, HelpMessage[]>>({});
    const [helpBusyFieldIds, setHelpBusyFieldIds] = useState<ReadonlySet<string>>(new Set());
    const [helpSlowFieldIds, setHelpSlowFieldIds] = useState<ReadonlySet<string>>(new Set());
    // Same reasoning as `stateRef` above, and updated the same way — written
    // synchronously at the mutation site, never via an effect (an effect
    // would be a render behind, exactly the bug `stateRef` exists to avoid):
    // a retry removes the error message and immediately re-runs the turn in
    // the same tick, and that turn's own thread-history slice has to see the
    // post-removal thread, not last render's.
    const helpThreadsRef = useRef<Record<string, HelpMessage[]>>({});
    const updateHelpThreads = useCallback(
        (updater: (prev: Record<string, HelpMessage[]>) => Record<string, HelpMessage[]>) => {
            helpThreadsRef.current = updater(helpThreadsRef.current);
            setHelpThreads(helpThreadsRef.current);
        },
        []
    );
    const closeFormView = useCallback(() => {
        setFormViewOpen(false);
        setReviewFormId(null);
        setHelpFieldId(null);
    }, []);
    const flipRectRef = useRef<DOMRect | null>(null);
    // Serializes turns so a message sent while another is in flight resolves
    // after it, with its receipts on its own reply (design: FIFO turn ordering).
    const turnChainRef = useRef<Promise<void>>(Promise.resolve());
    // Timed-out turns whose request is still running and whose error bubble is
    // still live (store tracker 6.29). Membership is the client half of the
    // "bubble still live" test: any newer turn — a Try again or a fresh
    // message — clears the whole set at enqueue time, because the conversation
    // has moved past every bubble that was waiting. The reducer's id lookup
    // covers the bubble's own removal.
    const lateTurnIdsRef = useRef<Set<string>>(new Set());
    // Forms whose collected-values read has been issued this session (store
    // tracker 6.26a): the mount-time hydration marks the forms it reads, and
    // activation marks before its own read — one read per form per session,
    // attempted-once whether or not the endpoint answered.
    const hydratedFormIdsRef = useRef<Set<string>>(new Set());
    // Forms-corrections slice (D3 hardening): the values an in-flight
    // `updateSubmission` actually transmitted, keyed by form id — a review
    // field stays editable through the write's ~1-2s round trip, so
    // `completeUpdate` needs the exact snapshot the POST carried (not
    // whatever `state.values` holds by the time the write resolves) to tell
    // a genuine mid-flight edit from a no-op re-render.
    const snapshotRef = useRef<Map<string, Record<string, FieldValue>>>(new Map());
    // Forward reference (store tracker 6.55): `activate`/`switchToForm` need
    // to enqueue the hidden activation turn, but that turn runner
    // (`runActivationTurn`) is defined later in this file — after `commitFills`
    // and `routeOffer`, which it depends on. Routing the call through a ref
    // instead of a direct closure reference avoids reordering ~600 lines of
    // interdependent `useCallback`s for what is otherwise a one-line call;
    // the ref is kept current by a plain assignment right after
    // `runActivationTurn`'s own definition, which runs on every render before
    // anything could invoke it.
    const runActivationTurnRef = useRef<(formId: string, fallbackText: string) => Promise<void>>(
        async () => undefined
    );

    const eventCtx = useMemo(
        () => ({ indexName: config.IndexName, sessionId: appState.sessionid, pageUrl: appState.pageUrl }),
        [config.IndexName, appState.sessionid, appState.pageUrl]
    );

    // Latest state for the async turn runner — a turn started before a confirm
    // must still send the post-confirm open-field list.
    //
    // React's state arrives here a render late: the reducer flushes, then an
    // effect copies it across. A turn queued behind another starts inside the
    // promise continuation, *before* that effect runs, so it would build its
    // request from a pre-commit view of the state — and ask extraction to fill a
    // field the turn ahead of it had already confirmed (design D5 forbids
    // exactly that). The reducer is pure, so replaying each action here as it is
    // dispatched keeps this ref authoritative at dispatch time rather than a
    // render later. The effect stays as the reconciler for anything that reaches
    // the reducer another way.
    const stateRef = useRef(state);
    useEffect(() => { stateRef.current = state; }, [state]);

    const dispatch = useCallback(
        (action: CfAction) => {
            stateRef.current = formsReducer(stateRef.current, action);
            persistDispatch(action);
        },
        [persistDispatch]
    );

    /** The form a turn (or write) belongs to, derived at call time so a queued
     *  turn follows a switch that landed ahead of it: the active form, or the
     *  config form before anything is started. */
    const formFor = useCallback(
        (s: CfState): CfForm | null =>
            s.activeFormId ? registry?.byId.get(s.activeFormId) ?? null : registry?.primary ?? null,
        [registry]
    );

    /* ---------------------------------------------------------------
       1.3 — collected-values read on mount, with start-from-empty fallback.
       The read is keyed by objectid, so multiform hydration is one read per
       form the visitor has a stake in: the config form (the server may hold
       values from another session this device never saw), plus every bot form
       locally started or queued. Untouched bot forms are not read — there is
       nothing to resume and nothing to gate.
    ----------------------------------------------------------------*/
    useEffect(() => {
        if (!active || !registry || state.hydrated) return;
        let cancelled = false;

        const local = stateRef.current;
        // A discarded form stays discarded across reloads (store tracker 6.20):
        // reading the collected values back would resurrect exactly what the
        // discard cleared, and the persist effect would then write the imported
        // values straight back to localStorage. Skip those reads entirely.
        const toRead = registry.forms.filter(
            (f) =>
                !local.discardedFormIds.includes(f.objectId) &&
                (f.objectId === registry.primary?.objectId ||
                    local.startedFormIds.includes(f.objectId) ||
                    local.queuedForms.includes(f.objectId))
        );
        // The mount read counts toward the once-per-session-per-form budget of
        // the activation hydration below (tracker 6.26a).
        toRead.forEach((f) => hydratedFormIdsRef.current.add(f.objectId));
        if (!toRead.length) {
            dispatch({ type: 'hydrate' });
            return;
        }

        const read = async () => {
            const results = await Promise.all(
                toRead.map(async (f) => {
                    try {
                        return {
                            form: f,
                            result: await api.forms.collected({
                                sessionid: appState.sessionid,
                                index: config.IndexName ?? '',
                                objectid: f.objectId,
                            }),
                        };
                    } catch (_) {
                        // Read unavailable → this form works from local state.
                        return { form: f, result: null };
                    }
                })
            );
            if (cancelled) return;

            const values: Record<string, FieldValue> = {};
            const discardedFormIds: string[] = [];
            const submittedFormIds: string[] = [];
            let resumeFormId: string | undefined;

            results.forEach(({ form: f, result }) => {
                if (!result) return;
                // The server can know a lifecycle state this device does not —
                // a dismiss or submit from another session. Its `dismissed` flag
                // is treated exactly like a local discard: nothing imported,
                // nothing pinned, and the id joins the local record so the next
                // reload never asks again (tracker 6.20).
                if (result.dismissed) {
                    discardedFormIds.push(f.objectId);
                    return;
                }
                // A submitted form (local record or server flag) is done: its
                // values still import — they serve the corrections slice — but
                // it must never re-pin as in progress. The post-submit UI is an
                // unpinned card, and a refresh must land on the same picture.
                const submitted =
                    result.submitted === true || local.submittedFormIds.includes(f.objectId);
                if (result.submitted === true) submittedFormIds.push(f.objectId);

                // The read returns the merged view as a list of envelopes; the
                // widget only needs id → value. `status` is provenance
                // (confirmed vs chat-filled) and both count as committed here,
                // and the percentage is computed from the form definition rather
                // than `total_fields` so conditional visibility is respected.
                let hasValues = false;
                (result.fields ?? []).forEach((entry) => {
                    if (entry?.field_id && entry.value != null && entry.value !== '') {
                        // A consent/checklist value comes back comma-joined
                        // (`toSaveFields`'s wire format is unchanged) — split
                        // it back into an array here so the rest of the app
                        // never sees the raw joined string (fix: mount hydration
                        // consent/checklist round-trip corruption).
                        const field = f.fields.find((ff) => ff.id === entry.field_id);
                        // Store tracker 6.64: an unsubmitted form's read is
                        // gap-fill only — a field the local map already holds a
                        // key for (a form-view edit that landed a beat before
                        // this read, or before the server's own write) must
                        // survive the reload untouched, mirroring
                        // `hydrateForm`'s per-key merge. A submitted form's
                        // read stays authoritative (unconditional overwrite) —
                        // it serves the corrections slice, where the server's
                        // record is the one that matters.
                        if (submitted || !(entry.field_id in local.values)) {
                            values[entry.field_id] = hydrateFieldValue(field, entry.value);
                        }
                        hasValues = true;
                    }
                });
                // A visitor with config-form values on the server is mid-form:
                // resume pinned. Bot forms resume only through the locally
                // persisted active id — the server offer decided their start.
                // Adversarial review F1(a), store tracker 6.55: never resume a
                // form the visitor already deliberately paused (✕ on the
                // pinned card) — a paused form sits in `local.queuedForms`,
                // and the server still reporting its mid-form values must not
                // silently re-pin it out from under that choice on reload
                // (the reducer's own invariant, F1(b), is the second line of
                // defense if this guard is ever bypassed).
                if (
                    hasValues &&
                    !submitted &&
                    f.objectId === registry.primary?.objectId &&
                    !local.queuedForms.includes(f.objectId)
                ) {
                    resumeFormId = f.objectId;
                }
            });

            dispatch({
                type: 'hydrate',
                values: Object.keys(values).length ? values : undefined,
                // Never stomp a persisted active form (a returning multiform
                // device): the resume only fills a fresh device's null.
                activeFormId:
                    local.activeFormId === null && resumeFormId ? resumeFormId : undefined,
                discardedFormIds: discardedFormIds.length ? discardedFormIds : undefined,
                submittedFormIds: submittedFormIds.length ? submittedFormIds : undefined,
            });
        };

        read();
        return () => { cancelled = true; };
    }, [active, registry, state.hydrated, config.IndexName, appState.sessionid, dispatch]);

    /* ---------------------------------------------------------------
       The save path — a commit re-posts the full field list (platform:
       precedence-safe save is server-side; the client re-sends what it holds).

       This is also what drives the save cue (harvest 0.3): the cue tracks
       the real POST — saving while it is in flight, saved on success, the
       local-retention state on failure — and nothing else. A plain local
       edit makes no claim, so it shows no cue; the demo's timer-driven
       "Saved" over unwritten state was exactly the lie this replaces.
    ----------------------------------------------------------------*/
    const persist = useCallback(
        async (
            values: Record<string, FieldValue>,
            chatOriginIds?: ReadonlySet<string>,
            // Forms-corrections slice: an explicit target overrides the
            // active-form default — a correction re-post targets the form
            // under review, which may not be the pinned/active one.
            targetFormId?: string
        ): Promise<boolean> => {
            // The write is keyed by the *active* form's objectid and carries
            // only that form's fields — never another form's (multiform rule).
            const target = targetFormId
                ? registry?.byId.get(targetFormId) ?? null
                : formFor(stateRef.current);
            if (!target) return false;
            // The cue carries the write's target form id (review-fix): a
            // correction re-post's target may differ from whatever form is
            // currently active/pinned, and the cue's one surface (the active
            // form's pinned card) must never claim a write that wasn't its own.
            dispatch({ type: 'setSaveCue', cue: 'saving', formId: target.objectId });
            try {
                await api.submitDyanmicInformation.submitDyanmicInformation({
                    indexname: config.IndexName,
                    sessionid: appState.sessionid,
                    objectid: target.objectId,
                    fields: toSaveFields(target, values, chatOriginIds),
                });
                dispatch({ type: 'setSaveCue', cue: 'saved', formId: target.objectId });
                return true;
            } catch (_) {
                // Local-first: values stay on the device and the next commit retries.
                dispatch({ type: 'setSaveCue', cue: 'error', formId: target.objectId });
                return false;
            }
        },
        [formFor, registry, config.IndexName, appState.sessionid, dispatch]
    );

    const addMessage = useCallback(
        (message: ChatMessage) => appDispatch({ type: 'addMessage', message }),
        [appDispatch]
    );

    /* ---------------------------------------------------------------
       Hydrate-on-activation (store tracker 6.26a). Chat answers for a
       non-active form persist server-side but are display-only client-side,
       so the server can hold values this device has never imported. When a
       form becomes active — start card, menu Start/Switch, queue-row Switch,
       first-fill activation — read its collected values once per session.
       Merge is gap-fill only (`hydrateForm`): a local value always wins, so
       a paused form's unsent manual edits are never stomped and a locally
       cleared field is never resurrected. The 6.20 gates hold: a locally
       discarded form is never read, and the server's `dismissed`/`submitted`
       flags join the local record exactly as mount hydration records them.
       A failed read leaves local state alone — same posture as the mount
       read's "endpoint missing" fallback.
    ----------------------------------------------------------------*/
    const hydrateFormOnActivation = useCallback(
        (formId: string) => {
            if (hydratedFormIdsRef.current.has(formId)) return;
            if (stateRef.current.discardedFormIds.includes(formId)) return;
            hydratedFormIdsRef.current.add(formId);
            api.forms
                .collected({
                    sessionid: appState.sessionid,
                    index: config.IndexName ?? '',
                    objectid: formId,
                })
                .then((result) => {
                    if (!result) return;
                    if (result.dismissed) {
                        dispatch({ type: 'hydrateForm', discardedFormIds: [formId] });
                        return;
                    }
                    const values: Record<string, FieldValue> = {};
                    const targetForm = registry?.byId.get(formId);
                    (result.fields ?? []).forEach((entry) => {
                        if (entry?.field_id && entry.value != null && entry.value !== '') {
                            // Same split-back-into-array normalization as mount
                            // hydration above — this path only has `formId`, so
                            // the field definition is resolved from the registry.
                            const field = targetForm?.fields.find((ff) => ff.id === entry.field_id);
                            values[entry.field_id] = hydrateFieldValue(field, entry.value);
                        }
                    });
                    dispatch({
                        type: 'hydrateForm',
                        values: Object.keys(values).length ? values : undefined,
                        submittedFormIds: result.submitted === true ? [formId] : undefined,
                    });
                })
                .catch(() => undefined);
        },
        [appState.sessionid, config.IndexName, dispatch, registry]
    );

    /**
     * Serializes turns so a message sent while another is in flight resolves
     * after it, with its receipts on its own reply (design: FIFO turn
     * ordering). Generalized over a thunk (store tracker 6.55) so both the
     * ordinary conversation (`runTurn`) and the hidden activation turn
     * (`runActivationTurn`) share one queue — a Switch tapped mid-turn must
     * not race the turn already in flight. Defined early enough in this file
     * that `activate`/`switchToForm` below can call it directly, unlike
     * `runActivationTurn` itself (see `runActivationTurnRef`'s note).
     */
    const enqueueTurn = useCallback((run: () => Promise<void>): Promise<void> => {
        // Any newer turn — a fresh message, a Try again, or a fresh
        // engagement — supersedes every timed-out turn still waiting on a
        // late response: the conversation has moved on, so a late original
        // must not render a reply out of order. Its fills still commit
        // through the guarded path (store tracker 6.29).
        lateTurnIdsRef.current.clear();
        const next = turnChainRef.current.then(run).catch(() => undefined);
        turnChainRef.current = next;
        return next;
    }, []);

    /* ---------------------------------------------------------------
       Forms-corrections slice — review entry (design D1). Deliberately not
       `activate`/`switchToForm`: a submitted form under review must never pin
       itself, pause whatever else is active, or touch the queue — it is a
       read of already-collected values, not a resumed task. The one thing it
       does share with activation is the once-per-session collected-values
       read, so a correction opens against the latest server state.
    ----------------------------------------------------------------*/
    const openReview = useCallback(
        (formId: string) => {
            if (!registry?.byId.get(formId)) return;
            // Defensive guard: review is a submitted-form concept. Every UI
            // entry (the menu's Review row, the pinned dirty-warning row)
            // already only exists for a submitted form, so this never fires
            // in practice — it exists so a future caller cannot open "review"
            // on a form that was never submitted.
            if (!stateRef.current.submittedFormIds.includes(formId)) return;
            setReviewFormId(formId);
            setFormViewOpen(true);
            hydrateFormOnActivation(formId);
        },
        [registry, hydrateFormOnActivation]
    );

    /* ---------------------------------------------------------------
       2.1 / 2.2 — activation
    ----------------------------------------------------------------*/
    /**
     * Pin `formId`, exactly once (platform: "Form activation is explicit").
     * Shared by every engagement path: an explicit Start tap
     * (`activationTurn: true` — send the hidden activation turn), and the
     * response's `form_activated` signal for an in-window offer acceptance
     * (`activationTurn: false` — the turn already in flight IS the composed
     * message; sending a second, nested one would be wrong).
     *
     * Adversarial review F2 (store tracker 6.55): the signal path takes a
     * server-named formId this function does not otherwise control, so it
     * needs the same lifecycle suppression `routeOffer` applies to an offer —
     * a submitted or discarded form must NEVER be re-pinned (their own UI
     * entry points, StartCard/menu Switch, already can't reach one, but the
     * signal has no such gate of its own). `active` is already a no-op via
     * the early return below; a queued or merely started (paused) form is
     * legitimate to activate — the dispatch below consumes its queue entry
     * exactly as a normal Start/Switch would (no separate "dedupe" needed).
     *
     * Adversarial review F4: dispatches `switchForm` (pausing whatever is
     * currently active to the front of the queue) rather than `startForm`
     * whenever another form is active — `startForm` alone never touches the
     * outgoing form at all, so a `form_activated` signal for form B while A
     * was pinned used to simply drop A with no queue entry, no pause, gone.
     */
    const activate = useCallback(
        (formId: string, options: { activationTurn: boolean }) => {
            const target = registry?.byId.get(formId);
            if (!target) return;
            const current = stateRef.current;
            if (current.activeFormId === formId) return;
            if (current.submittedFormIds.includes(formId) || current.discardedFormIds.includes(formId)) {
                return;
            }
            // `FormStarted` fires exactly once per form (platform single-emitter
            // rule) — a resume of a paused form is not a second start.
            const alreadyStarted = current.startedFormIds.includes(formId);
            dispatch(
                current.activeFormId !== null
                    ? { type: 'switchForm', formId }
                    : { type: 'startForm', formId }
            );
            // 1.3 — recomputes progress from the collected read: ambient
            // fills accrued while the form was inactive hydrate immediately.
            hydrateFormOnActivation(formId);
            if (!alreadyStarted) logFormStarted(formId, eventCtx);
            if (options.activationTurn) {
                enqueueTurn(() => runActivationTurnRef.current(formId, target.startMessage));
            }
        },
        [registry, dispatch, hydrateFormOnActivation, eventCtx, enqueueTurn]
    );

    const startForm = useCallback(
        (formId: string, rect?: DOMRect | null) => {
            flipRectRef.current = rect ?? null;
            activate(formId, { activationTurn: true });
        },
        [activate]
    );

    /* ---------------------------------------------------------------
       Multiform switching (design D2/D5): pause the active form to the front
       of the queue and activate the target. 2.2 (store tracker 6.55): the
       transition is narrated by the hidden activation turn's own
       server-composed reply — the client-posted "Switched to…" acknowledgement
       is removed. Values never move — per-form progress derives from the
       shared map (design D1).

       Store tracker 6.56: every existing caller (menu Switch, queue-row
       Switch, pinned-card resume) already only ever offers a switchable
       form — a submitted or discarded one has no Switch affordance to tap.
       `ReviewSubmitCard`'s Review action broke that invariant: it hands this
       function a server-named formId it does not otherwise control (same
       shape as the `form_activated` signal `activate` already guards below),
       so this needs its own copy of `activate`'s lifecycle suppression —
       must NEVER re-pin a submitted or discarded form, belt-and-braces
       alongside that card's own guard.
    ----------------------------------------------------------------*/
    const switchToForm = useCallback(
        (formId: string) => {
            const target = registry?.byId.get(formId);
            if (!target) return;
            const current = stateRef.current;
            if (current.activeFormId === formId) return;
            if (current.submittedFormIds.includes(formId) || current.discardedFormIds.includes(formId)) {
                return;
            }
            const paused = current.activeFormId ? registry?.byId.get(current.activeFormId) ?? null : null;
            const alreadyStarted = current.startedFormIds.includes(formId);
            dispatch({ type: 'switchForm', formId });
            hydrateFormOnActivation(formId);
            if (!alreadyStarted) logFormStarted(formId, eventCtx);
            // The local acknowledgement text survives only as the graceful
            // degrade for a backend that doesn't (yet) answer the activation
            // turn meaningfully — see `runActivationTurn`.
            const fallback = paused
                ? switchAcknowledgement(target.short, paused.short)
                : target.startMessage;
            enqueueTurn(() => runActivationTurnRef.current(formId, fallback));
        },
        [registry, dispatch, hydrateFormOnActivation, eventCtx, enqueueTurn]
    );

    /**
     * Engagement closure (store tracker 6.55): the pinned card's ✕ pauses
     * rather than discards — unpin to the front of the queue, touch no data,
     * post no acknowledgement (pausing is silent by design; only Start/Switch
     * and discard/submit narrate in chat). Resume is a normal `switchToForm`.
     */
    const pauseForm = useCallback(() => dispatch({ type: 'pauseForm' }), [dispatch]);

    const removeQueuedForm = useCallback(
        // Quiet by design: no dismiss POST, no chat line — a queue removal is
        // not a discard, and a later trigger may legitimately re-offer.
        (formId: string) => dispatch({ type: 'removeQueued', formId }),
        [dispatch]
    );

    /**
     * Shared commit path for a turn's fills — used by both the `/question/`
     * response and the stream's terminal event so the two channels behave
     * identically (auto commits and saves, suggested parks the values pending).
     *
     * Adversarial review F3 (store tracker 6.55): `ownerFormId` is a required
     * argument, not derived internally — every caller MUST bind it to the
     * form that owned the turn (captured before the turn's own await, reused
     * as-is at commit time), never re-derive "whatever is active now". A
     * turn sent while form X was active still belongs to X even if the
     * visitor pauses (or otherwise changes what's pinned) while the request
     * is in flight; re-deriving at commit time silently dropped those fills
     * (they'd normalize as a foreign/display-only fill against whatever
     * form — or none — is current by the time the response lands).
     */
    const commitFills = useCallback(
        (ownerFormId: string | undefined, rawFills: Parameters<typeof normalizeFills>[1]) => {
            const turnForm = ownerFormId ? registry?.byId.get(ownerFormId) ?? null : null;
            if (!turnForm) return undefined;
            const fills = normalizeFills(turnForm, rawFills, registry);
            if (!fills.length) return undefined;

            // A withheld fill has a receipt but no value to store, and a fill
            // belonging to another form (registry or truly foreign) is
            // display-only — neither reaches `values`, so neither moves the
            // turn form's progress or next-required.
            const committable = committableFills(fills);
            // Store tracker 6.64: captured before the dispatch below, so the
            // shield check below sees the same pre-write state the reducer's
            // own `acceptsAutoFill` filter will evaluate against — dispatch
            // mutates `stateRef.current` synchronously (see the `dispatch`
            // wrapper above), so reading it any later would race the very
            // write we're trying to predict.
            const preState = stateRef.current;
            if (committable.length) {
                dispatch({
                    type: 'applyFills',
                    fills: committable.map((f) => ({ fieldId: f.fieldId, value: f.value })),
                    mode: turnForm.fillMode,
                });
            }
            // Engagement closure (store tracker 6.55): a fill alone never
            // activates a form anymore — implicit first-fill activation is
            // removed. A fill against a not-yet-active form commits and
            // receipts exactly as any ambient fill does; pinning happens only
            // from an explicit Start/Switch tap or the response's
            // `form_activated` signal (handled by the turn runners below).
            // In auto mode the backend has already written the values at
            // extraction; re-posting them here would only race its own record.
            // The save path is for the student's own confirms and edits.
            //
            // Store tracker 6.64: a fill the shield above just refused (a
            // form-view-confirmed field, still valid) never reaches `values` —
            // it must not get a receipt either, or the chat would claim a
            // write that never happened. `acceptsAutoFill` is the exact
            // predicate the reducer applied, so this can never disagree with
            // it. Suggested-mode fills are never shielded (the reducer parks
            // them regardless of confirmation), so nothing here touches them.
            const refusedIds =
                turnForm.fillMode === 'auto'
                    ? new Set(
                          committable
                              .filter((f) => !acceptsAutoFill(preState, f.fieldId))
                              .map((f) => f.fieldId)
                      )
                    : undefined;
            return toReceipts(refusedIds?.size ? fills.filter((f) => !refusedIds.has(f.fieldId)) : fills);
        },
        [registry, dispatch]
    );

    /* ---------------------------------------------------------------
       Field help (slice 5, design D1/D5). A help turn is a normal `/question/`
       turn with three differences from the main conversation (the adopted v1
       transport, store tracker 6.35): the question carries the demo's
       parenthetical preamble, `open_fields` holds exactly the scoped field, and
       `form_objectid` is always set. Response handling is fill-first — a fill
       for the scoped field applies through the SAME `commitFills` the main
       turn uses (mode-aware: suggested mode parks it pending), and renders a
       receipt in-thread; no fill renders the reply as the explanation. One
       code path, no intent classifier.

       This is a thread-scoped runner, not a call into `runTurn`/`enqueueTurn`:
       the FIFO chain those use serializes with and posts into the main
       conversation, and a help turn must never do either (design D5 —
       "a thread failure never affects the main conversation or other
       threads"). It mirrors `runTurn`'s latency/timeout/error handling with
       the same constants (`SLOW_TURN_MS`, `TURN_TIMEOUT_MS`, `MIN_THINKING_MS`)
       and the same retryable-error-bubble copy, scoped to `helpThreads`
       instead of `AppState.messages`.
    ----------------------------------------------------------------*/
    const appendHelpMessage = useCallback(
        (fieldId: string, message: HelpMessage) => {
            updateHelpThreads((prev) => ({ ...prev, [fieldId]: [...(prev[fieldId] ?? []), message] }));
        },
        [updateHelpThreads]
    );

    const openHelp = useCallback((fieldId: string) => setHelpFieldId(fieldId), []);
    const closeHelp = useCallback(() => setHelpFieldId(null), []);

    /**
     * Is `fieldId` currently extraction-eligible on `targetForm`? Re-derives
     * from `openForAI` against the freshest state (`stateRef`, not the
     * `state` closure) rather than trusting whatever the UI last rendered —
     * the send-time guard (adversarial-review B1c) this backs must hold even
     * if a render raced ahead of it. `openForAI` excludes `confirmed` but NOT
     * `suggested` (adversarial-review R1b) — a pending suggestion is exactly
     * one of the states the composer must lock for (design D6, "Confirm/Undo
     * owns that moment"), so this has to mirror FormOverlay's render-time
     * `eligibleNow` exactly, not just `openForAI` alone, or a suggested-mode
     * retry could still fire and silently overwrite the pending suggestion.
     * Adversarial-review N6: `statusOf` takes the field definition too (it
     * withholds `confirmed` for a structurally-incomplete `consent`/`group`)
     * — FormOverlay's render-time call always passes it, so this does too,
     * making the two predicates equivalent BY CONSTRUCTION rather than only
     * agreeing today because `consent`/`group` happen to also be `noAI`.
     */
    const helpFieldEligibleNow = useCallback(
        (targetForm: CfForm, fieldId: string): boolean => {
            const fieldDef = targetForm.fields.find((f) => f.id === fieldId);
            return (
                statusOf(stateRef.current, fieldId, fieldDef) !== 'suggested' &&
                openForAI(targetForm, stateRef.current, registry).some((f) => f.id === fieldId)
            );
        },
        [registry]
    );

    /**
     * Is the form a help turn was sent for still a legitimate commit target?
     * (Adversarial-review M3/M4.) An in-flight turn can outlive a submit or a
     * discard by as much as `TURN_TIMEOUT_MS` — committing after either would
     * write into a form with no dirty flag, no re-post, no `FormUpdated` (the
     * exact D7 bypass), or resurrect a form the student just discarded.
     * Checked both on the normal path and the late-timeout path, right before
     * any commit.
     */
    const ownerFormLive = useCallback(
        (ownerFormId: string): boolean => {
            const s = stateRef.current;
            if (s.submittedFormIds.includes(ownerFormId)) return false;
            if (s.discardedFormIds.includes(ownerFormId)) return false;
            // Not just "some registry form" — the form this turn was scoped
            // to must still be the one actually active; a switch mid-flight
            // means the turn's field no longer belongs to what's current.
            if (formFor(s)?.objectId !== ownerFormId) return false;
            return true;
        },
        [formFor]
    );

    /**
     * Runs one help turn against `text` — extraction, timeout/error handling,
     * fill-or-explain rendering. Deliberately does NOT append the student's
     * own message: `sendHelp` (the public entry point, below) does that once,
     * at the original send, and `retryHelp` re-runs THIS directly so a retry
     * never re-adds a second copy of the message that's already sitting in
     * the thread — the same split `runTurn` (never appends the user bubble)
     * and `sendTurn` (appends it once, before enqueuing) keep in the main
     * conversation.
     */
    const runHelpTurn = useCallback(
        async (fieldId: string, text: string) => {
            // The field's own form: help is only ever offered on the current
            // presentation form's rows (never in review mode — design D7 hides
            // the affordance there entirely), so `form` is always the right
            // scope, exactly like the main turn's `formFor(current)`.
            const helpForm = form;
            const helpFieldDef = helpForm?.fields.find((f) => f.id === fieldId);
            if (!helpForm || !helpFieldDef) {
                // Adversarial-review m7: a field-resolution miss must not
                // leave the student's just-appended message stranded with no
                // response — the same retryable bubble the normal failure
                // path renders below. `sendHelp`/`retryHelp` already guard
                // eligibility before calling in, so this is a defensive
                // backstop against a future caller, not a reachable path
                // today.
                appendHelpMessage(fieldId, {
                    role: 'bot',
                    text: TURN_ERROR_MESSAGE,
                    error: true,
                    retryText: text,
                });
                return;
            }

            // Adversarial-review M3/M4: the form this turn is scoped to,
            // captured now. Bail before ever sending if it's already out of
            // scope — submitted (a help fill on a submitted form is exactly
            // the corrections-mode side door design D7 forbids) or currently
            // open for review.
            const ownerFormId = helpForm.objectId;
            if (stateRef.current.submittedFormIds.includes(ownerFormId) || reviewFormId === ownerFormId) {
                appendHelpMessage(fieldId, {
                    role: 'bot',
                    text: TURN_ERROR_MESSAGE,
                    error: true,
                    retryText: text,
                });
                return;
            }

            setHelpBusyFieldIds((prev) => withId(prev, fieldId));

            // Verbatim from the demo baseline (`sendHelp`, forms-demo
            // `FormOverlay.tsx`) — the field is spotlighted in the question
            // itself so retrieval and the answer stay on subject.
            const preamble =
                `(The visitor is asking about the "${helpFieldDef.label}" field on the ` +
                `${helpForm.name}${helpFieldDef.help ? ` — ${helpFieldDef.help}` : ''}. It is ` +
                `${helpFieldDef.optional ? 'optional' : 'required'}. Answer briefly, about ` +
                `this field.) ${text}`;

            // Thread's own history plus recent chat rides as context so "it"
            // resolves, same as the demo baseline. Read off the ref, not the
            // `helpThreads` state closure — this can run inside the same tick
            // as a just-applied removal (retry), a render before that removal
            // would otherwise be visible here.
            const threadHistory = (helpThreadsRef.current[fieldId] ?? []).slice(-4).map((m) => ({
                content: m.text,
                direction: m.role === 'user' ? 0 : 1,
                time: new Date(Date.now()),
            }));
            const chatHistory = appState.messages.filter((m) => !m.temp).slice(-2);

            const slowTimer = setTimeout(
                () => setHelpSlowFieldIds((prev) => withId(prev, fieldId)),
                SLOW_TURN_MS
            );
            const startedAt = Date.now();

            let result: Awaited<ReturnType<typeof api.question.ask>> | null = null;
            // Held outside the race so a timed-out turn keeps a handle on the
            // still-running request (mirrors `runTurn`'s store-tracker-6.29
            // reasoning, narrowed: see the fill-safety note below).
            let askPromise: ReturnType<typeof api.question.ask> | null = null;
            let timedOut = false;
            let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
            try {
                askPromise = api.question.ask(
                    {
                        question: preamble,
                        index: config.IndexName,
                        context: [...chatHistory, ...threadHistory],
                        sessionid: appState.sessionid,
                        // Adversarial-review m1: `runTurn` sends this too —
                        // the contract names exactly three differences from a
                        // normal turn, and this isn't meant to be a fourth.
                        current_url: appState.pageUrl || undefined,
                        open_fields: toOpenFieldSpecs([helpFieldDef]),
                        form_objectid: helpForm.objectId,
                        // Store tracker 6.64: the visitor's form-view-confirmed
                        // fields ride every turn while the feature is active
                        // (this turn only runs when `registry` is non-null).
                        confirmed_field_ids: stateRef.current.confirmedFieldIds,
                    },
                    config.CustomPrompt ?? false
                );
                result = await Promise.race([
                    askPromise,
                    new Promise<never>((_, reject) => {
                        timeoutTimer = setTimeout(() => {
                            timedOut = true;
                            reject(new Error('timeout'));
                        }, TURN_TIMEOUT_MS);
                    }),
                ]);
            } catch (_) {
                result = null;
            }

            clearTimeout(timeoutTimer);
            clearTimeout(slowTimer);
            setHelpSlowFieldIds((prev) => withoutId(prev, fieldId));

            const elapsed = Date.now() - startedAt;
            if (elapsed < MIN_THINKING_MS) await sleep(MIN_THINKING_MS - elapsed);

            const outcome = classifyTurn(result);
            if (!result || outcome === 'retryable') {
                // The student's message stays in the thread; only this turn
                // resolves to the bubble — same isolation contract as the main
                // conversation's retryable surface (design D5).
                appendHelpMessage(fieldId, {
                    role: 'bot',
                    text: timedOut ? TURN_TIMEOUT_MESSAGE : TURN_ERROR_MESSAGE,
                    error: true,
                    retryText: text,
                });
                setHelpBusyFieldIds((prev) => withoutId(prev, fieldId));
                // A client timeout does not mean the backend gave up, and in
                // auto mode it may still persist a fill server-side. Unlike
                // `runTurn`'s late reconciliation this never renders a late
                // REPLY into the thread (out of scope here — the bubble
                // already offers Try again) — but a late FILL still commits
                // and gets its own receipt line (adversarial-review M6), so
                // the write is never invisible even though the bubble above
                // still says "try again".
                if (timedOut && askPromise) {
                    askPromise
                        .then((late) => {
                            // Mirrors `reconcileLateTurn`'s own guard: a late
                            // retryable response has nothing to commit.
                            if (classifyTurn(late) === 'retryable') return;
                            // M3/M4: the owning form may have been submitted,
                            // discarded, or superseded while this was still
                            // in flight — never commit into it after the fact.
                            if (!ownerFormLive(ownerFormId)) return;
                            const lateReceipts = commitFills(ownerFormId, late.fills);
                            const lateFills = toHelpFills(lateReceipts, fieldId);
                            if (lateFills?.length) {
                                const lateFillFallback =
                                    helpForm.fillMode === 'suggested'
                                        ? HELP_FILL_FALLBACK_SUGGESTED
                                        : HELP_FILL_FALLBACK_AUTO;
                                appendHelpMessage(fieldId, {
                                    role: 'bot',
                                    text: late.answer || lateFillFallback,
                                    fills: lateFills,
                                });
                            }
                        })
                        .catch(() => undefined);
                }
                return;
            }

            // Adversarial-review M3/M4: the owning form may have left scope
            // (submitted, discarded, or no longer active) during the turn's
            // own flight even though it resolved on time — drop the fill
            // rather than commit into, or narrate a fill for, a form that is
            // no longer this turn's to write into. Adversarial-review R7:
            // unlike the late-timeout path, this turn never got an error
            // bubble of its own — silently returning here would strand the
            // student's message with no response at all. Retrying re-checks
            // eligibility (`helpFieldEligibleNow`) and, since R1a locks the
            // composer instead, cannot loop.
            if (!ownerFormLive(ownerFormId)) {
                appendHelpMessage(fieldId, {
                    role: 'bot',
                    text: TURN_ERROR_MESSAGE,
                    error: true,
                    retryText: text,
                });
                setHelpBusyFieldIds((prev) => withoutId(prev, fieldId));
                return;
            }

            // Fill-first (D1): a non-withheld wire fill for the scoped field
            // means this turn filled it — the "filled" framing. A withheld
            // one (adversarial-review m5) still gets its own name-only
            // receipt below (mirrors `FillReceipts`' sensitive treatment),
            // just without claiming a fill happened.
            const rawScopedFill = result.fills?.find((f) => f.field_id === fieldId);
            const scopedHasValue = !!rawScopedFill && rawScopedFill.field_value != null;
            const receipts = commitFills(ownerFormId, result.fills);
            // Adversarial-review M7: every receipt the turn returned renders,
            // not just the scoped field's — auto mode already persisted all
            // of them server-side, so dropping any client-side would diverge
            // silently from the record.
            const fills = toHelpFills(receipts, fieldId);

            appendHelpMessage(fieldId, {
                role: 'bot',
                text: scopedHasValue
                    ? result.answer ||
                      // Adversarial-review m6: mode-aware — a suggested-mode
                      // fill is not committed yet, so "I filled that in" would
                      // be false.
                      (helpForm.fillMode === 'suggested' ? HELP_FILL_FALLBACK_SUGGESTED : HELP_FILL_FALLBACK_AUTO)
                    : result.answer,
                fills,
                // Adversarial-review M5: extraction failed unretryably — the
                // reply stands, but the same quiet notice the main
                // conversation shows belongs here too, not silent success.
                unrecordedNotice: outcome === 'unrecorded' ? (receipts?.length ? 'partial' : 'full') : undefined,
                // Adversarial review F7: the mirror image — extraction failed
                // in a way the server guarantees is safe to re-run. Never set
                // alongside `unrecordedNotice` (one turn, one outcome).
                fillRetryText: fillsRetryable(result) ? text : undefined,
            });
            setHelpBusyFieldIds((prev) => withoutId(prev, fieldId));
        },
        [
            form,
            reviewFormId,
            appendHelpMessage,
            appState.messages,
            appState.sessionid,
            appState.pageUrl,
            config.IndexName,
            config.CustomPrompt,
            commitFills,
            ownerFormLive,
        ]
    );

    const sendHelp = useCallback(
        (fieldId: string, text: string): boolean => {
            const helpForm = form;
            // Adversarial-review B1(c), send-time guard: re-derive the scoped
            // field's eligibility right now rather than trust whatever the UI
            // last rendered — if it's not currently extraction-eligible
            // (confirmed, a pending suggestion, hidden, or opted out of bot
            // collection), refuse outright. No message is appended and no
            // request goes out. Adversarial-review R6: the return value tells
            // the composer whether the send was accepted, so a refused send
            // never clears the student's draft.
            if (!helpForm || !helpFieldEligibleNow(helpForm, fieldId)) return false;
            appendHelpMessage(fieldId, { role: 'user', text });
            // Adversarial-review N1: the accept signal must be synchronous —
            // the composer clears its draft the instant a send is accepted,
            // the same tick as every other composer in the widget, not after
            // the turn (1s–35s) resolves. Awaiting here would (1) hold the
            // sent text in the input the whole time, (2) wipe a DIFFERENT
            // draft the student typed into the same field while this turn was
            // still in flight when the reply eventually lands and clears it,
            // and (3) still clear the draft after a visibly failed 35s
            // timeout. `runHelpTurn` runs detached — nothing here consumes
            // its promise; it drives `helpThreads`/`helpBusyFieldIds` on its
            // own via `setState`, same as `retryHelp` already does.
            void runHelpTurn(fieldId, text);
            return true;
        },
        [form, helpFieldEligibleNow, appendHelpMessage, runHelpTurn]
    );

    const retryHelp = useCallback(
        (fieldId: string, index: number, text: string) => {
            const helpForm = form;
            // Same send-time guard as `sendHelp` (B1c): if the field became
            // ineligible between the failed attempt and this retry, leave the
            // error bubble as-is rather than send a request extraction would
            // reject anyway.
            if (!helpForm || !helpFieldEligibleNow(helpForm, fieldId)) return;
            // Removes only the error bubble — the student's own message,
            // already in the thread from the original send, is untouched.
            updateHelpThreads((prev) => ({
                ...prev,
                [fieldId]: (prev[fieldId] ?? []).filter((_, i) => i !== index),
            }));
            runHelpTurn(fieldId, text);
        },
        [form, helpFieldEligibleNow, updateHelpThreads, runHelpTurn]
    );

    /**
     * Offer routing, shared by the on-time reply and the late-reconciled one.
     * The server never signals an offer for a started, submitted, or dismissed
     * form; this local check covers the gestures it cannot see yet (a Start
     * tap or queue entry earlier in this same session) and makes any late
     * offer a no-op. When a form is active, an offer for another registry form
     * queues behind it — one sticky form; the reply's own prose narrates the
     * nomination and the Up next row is the UI. Only what the server actually
     * offered is ever queued, and only definitions the registry holds (an
     * unknown id attaches and renders nothing, as before). Returns the form id
     * to render as an inline start card, when that is the routing.
     */
    const routeOffer = useCallback(
        (offeredId: string | undefined): string | undefined => {
            if (!offeredId || !registry) return undefined;
            // An ambient (non-browsable) form is never visitor-startable
            // (store tracker 6.30): an offer naming one renders no card and
            // is never admitted to the up-next queue — with no form active it
            // simply renders nothing. Its fills, receipts, and hydration are
            // untouched; only the start surfaces are gated.
            const offered = registry.byId.get(offeredId);
            if (offered && !offered.browsable) return undefined;
            const current = stateRef.current;
            const suppressed =
                current.activeFormId === offeredId ||
                current.queuedForms.includes(offeredId) ||
                current.startedFormIds.includes(offeredId) ||
                current.submittedFormIds.includes(offeredId) ||
                current.discardedFormIds.includes(offeredId);
            if (suppressed) return undefined;
            if (current.activeFormId !== null) {
                if (registry.byId.has(offeredId)) {
                    dispatch({ type: 'queueForm', formId: offeredId });
                }
                return undefined;
            }
            return offeredId;
        },
        [registry, dispatch]
    );

    /**
     * `form_queued` (wire-contract update, store tracker 6.55): the server
     * now emits this exactly once, at the moment a trigger-matched form
     * queues behind the active one — the idempotent server no longer
     * re-signals `form_offer` for a non-active form on every turn, so that
     * repeated-offer mechanism no longer feeds the queue tray. This is a
     * direct queue, not an offer: no start-card routing, just the tray.
     * `queueForm`'s own suppression (active/queued/started/submitted/
     * discarded) makes this idempotent by construction — a second identical
     * signal is a no-op, matching the same ambient/non-browsable gate
     * `routeOffer` applies. Feature-detected: absent/null does nothing, and
     * a legacy backend's old `form_offer`-driven queueing (still handled by
     * `routeOffer` above) keeps working alongside it without double-queueing.
     */
    const routeQueued = useCallback(
        (queuedId: string | undefined) => {
            if (!queuedId || !registry) return;
            const queued = registry.byId.get(queuedId);
            if (!queued || !queued.browsable) return;
            dispatch({ type: 'queueForm', formId: queuedId });
        },
        [registry, dispatch]
    );

    /**
     * A timed-out turn's response arrived after all (store tracker 6.29): the
     * 15s timer used to race a pipeline that legitimately commits in 20–30s,
     * and the real reply — answer, receipts, offer — was discarded on the
     * floor. Runs detached from the FIFO turn chain, which released at
     * timeout; nothing here re-blocks or reorders it.
     *
     * The reply's surface renders only while the turn's error bubble is still
     * live: `lateTurnIdsRef` says no newer turn superseded it, and the
     * reducer's id lookup says a Try again did not remove it. A dead bubble
     * drops the surface — a reply never renders twice. The fills commit either
     * way: the backend already persisted them at extraction, and the reducer's
     * auto-mode guard (6.21) keeps a late fill from overwriting a field a
     * newer turn confirmed. A late *failure* never reaches here — the bubble
     * already offers Try again, so it changes nothing.
     */
    const reconcileLateTurn = useCallback(
        (
            turnId: string,
            text: string,
            ownerFormId: string | undefined,
            result: Awaited<ReturnType<typeof api.question.ask>>
        ) => {
            const live = lateTurnIdsRef.current.delete(turnId);
            const outcome = classifyTurn(result);
            if (outcome === 'retryable') return;
            // F3: the same owner the original turn was bound to at send
            // time, not whatever is active now that the late reply landed.
            const receipts = commitFills(ownerFormId, result.fills);
            // Engagement closure (store tracker 6.55): the write already
            // happened server-side by the time this resolves, so activation
            // and a declined offer apply regardless of whether the bubble is
            // still live — same posture as `commitFills` just above.
            if (
                result.form_activated &&
                registry?.byId.get(result.form_activated) &&
                stateRef.current.activeFormId !== result.form_activated
            ) {
                activate(result.form_activated, { activationTurn: false });
            }
            if (result.form_declined) dispatch({ type: 'removeQueued', formId: result.form_declined });
            routeQueued(result.form_queued ?? undefined);
            if (!live) return;
            const cardOfferId = routeOffer(result.form_offer ?? undefined);
            appDispatch({
                type: 'reconcileTurn',
                turnId,
                message: {
                    content: result.answer,
                    direction: 1,
                    time: new Date(Date.now()),
                    relatedLinks: result.links,
                    cards: result.cards,
                    fills: receipts?.length ? receipts : undefined,
                    formOfferId: cardOfferId,
                    formCompleteId: result.form_complete ?? undefined,
                    fillRetryText: fillsRetryable(result) ? text : undefined,
                    fillNotice:
                        outcome === 'unrecorded' ? (receipts?.length ? 'partial' : true) : undefined,
                },
            });
        },
        [commitFills, routeOffer, routeQueued, appDispatch, registry, activate, dispatch]
    );

    /**
     * The hidden activation turn (2.2, store tracker 6.55): a client-sent,
     * visitor-invisible turn through the standard `/question/` pipeline
     * carrying `activate_form`/`form_objectid` for `formId` and an empty
     * question. The response is one server-composed message that both
     * acknowledges the activation and asks the active form's next field (or
     * carries the completion signal when it was already complete ambiently)
     * — this replaces the client-posted "Switched to…"/start acknowledgement.
     *
     * `fallbackText` is the graceful degrade (task 2.2's "if the backend
     * doesn't support it… degrade gracefully rather than leaving a dead
     * end"): a legacy backend that does not yet recognize `activate_form`
     * answers with nothing usable, and rather than leave the typing
     * indicator a dead end or show a scary retry card for a message the
     * student never typed, the old local acknowledgement text stands in.
     * The caller (`activate`/`switchToForm`) has already pinned the form
     * synchronously before this runs — this only produces the chat message.
     */
    const runActivationTurn = useCallback(
        async (formId: string, fallbackText: string) => {
            if (!registry) return;

            // Typing indicator: an empty temp bot message is what the existing
            // Message component renders as MessageSending.
            addMessage({ content: '', direction: 1, time: new Date(Date.now()), temp: true });
            dispatch({ type: 'setThinking', thinking: true });
            const slowTimer = setTimeout(() => dispatch({ type: 'setSlowTurn', slowTurn: true }), SLOW_TURN_MS);
            const startedAt = Date.now();

            const targetForm = registry.byId.get(formId) ?? null;
            const history = appState.messages.filter((m) => !m.temp).slice(-4);

            let result: Awaited<ReturnType<typeof api.question.ask>> | null = null;
            // Held outside the race, exactly like `runTurn` (store tracker
            // 6.29) — adversarial review F8: a degrade-gracefully timeout
            // must not mean a late-arriving fill, `form_queued`, or
            // `form_complete` is silently lost; the request is still tracked
            // for late reconciliation.
            let askPromise: ReturnType<typeof api.question.ask> | null = null;
            let timedOut = false;
            let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
            try {
                askPromise = api.question.ask(
                    {
                        question: '',
                        index: config.IndexName,
                        context: history,
                        sessionid: appState.sessionid,
                        current_url: appState.pageUrl || undefined,
                        open_fields: toOpenFieldSpecs(
                            targetForm ? openForAI(targetForm, stateRef.current, registry) : []
                        ),
                        form_objectid: formId,
                        activate_form: formId,
                        // Store tracker 6.64: see the ordinary turn's own note
                        // below — sent on every forms-active turn, this hidden
                        // activation turn included.
                        confirmed_field_ids: stateRef.current.confirmedFieldIds,
                    },
                    config.CustomPrompt ?? false
                );
                result = await Promise.race([
                    askPromise,
                    new Promise<never>((_, reject) => {
                        timeoutTimer = setTimeout(() => {
                            timedOut = true;
                            reject(new Error('timeout'));
                        }, TURN_TIMEOUT_MS);
                    }),
                ]);
            } catch (_) {
                result = null;
            }

            clearTimeout(timeoutTimer);
            clearTimeout(slowTimer);
            dispatch({ type: 'setSlowTurn', slowTurn: false });

            const elapsed = Date.now() - startedAt;
            if (elapsed < MIN_THINKING_MS) await sleep(MIN_THINKING_MS - elapsed);

            const outcome = classifyTurn(result);
            if (!result || outcome === 'retryable') {
                // Degrade gracefully: no error card, no retry (there is no
                // visitor-typed text to retry) — the local acknowledgement
                // still narrates the transition so the moment never reads as
                // a dead end. `unaskedAck` keeps it out of the quick-reply
                // chips' eligibility (it asks no field, unlike a real reply) —
                // covers both branches here: an empty/retryable answer and a
                // transport failure or timeout (the `!result` case) alike.
                addMessage({ content: fallbackText, direction: 1, time: new Date(Date.now()), unaskedAck: true });
                dispatch({ type: 'setThinking', thinking: false });
                // F8: a client timeout does not mean the backend gave up —
                // reconcile a late response through the same path `runTurn`
                // uses, bound to THIS form (the activation turn always has
                // exactly one, unambiguous owner). The re-pin half of a late
                // `form_activated` is a no-op here in practice (this turn's
                // own `formId` is already active by the time it was sent),
                // and is guarded regardless by F2's lifecycle suppression.
                if (timedOut && askPromise) {
                    const turnId = uuidv4();
                    lateTurnIdsRef.current.add(turnId);
                    askPromise.then(
                        (late) => reconcileLateTurn(turnId, '', formId, late),
                        () => lateTurnIdsRef.current.delete(turnId)
                    );
                }
                return;
            }

            const receipts = commitFills(formId, result.fills);
            if (result.form_declined) dispatch({ type: 'removeQueued', formId: result.form_declined });
            routeQueued(result.form_queued ?? undefined);
            const cardOfferId = routeOffer(result.form_offer ?? undefined);
            addMessage({
                content: result.answer,
                direction: 1,
                time: new Date(Date.now()),
                relatedLinks: result.links,
                cards: result.cards,
                fills: receipts?.length ? receipts : undefined,
                formOfferId: cardOfferId,
                formCompleteId: result.form_complete ?? undefined,
                fillNotice: outcome === 'unrecorded' ? (receipts?.length ? 'partial' : true) : undefined,
            });
            dispatch({ type: 'setThinking', thinking: false });
        },
        [
            registry,
            addMessage,
            dispatch,
            appState.messages,
            appState.sessionid,
            appState.pageUrl,
            config.IndexName,
            config.CustomPrompt,
            commitFills,
            routeOffer,
            routeQueued,
            reconcileLateTurn,
        ]
    );
    // Kept current every render — see `runActivationTurnRef`'s declaration
    // for why `activate`/`switchToForm` call through the ref instead of this
    // closure directly.
    runActivationTurnRef.current = runActivationTurn;

    /* ---------------------------------------------------------------
       2.3 / 2.5 / 3.1 / 3.2 — the turn
    ----------------------------------------------------------------*/
    const runTurn = useCallback(
        async (text: string) => {
            if (!registry) return;

            // Typing indicator: an empty temp bot message is what the existing
            // Message component renders as MessageSending.
            addMessage({ content: '', direction: 1, time: new Date(Date.now()), temp: true });
            dispatch({ type: 'setThinking', thinking: true });
            const slowTimer = setTimeout(() => dispatch({ type: 'setSlowTurn', slowTurn: true }), SLOW_TURN_MS);
            const startedAt = Date.now();

            const current = stateRef.current;
            // The active form drives the turn (multiform rule): its open fields
            // and only its — never another form's — plus the config form before
            // anything starts, exactly as before.
            const turnForm = formFor(current);
            const history = appState.messages.filter((m) => !m.temp).slice(-4);

            let result: Awaited<ReturnType<typeof api.question.ask>> | null = null;
            // Held outside the race so a timed-out turn keeps a handle on the
            // still-running request — that is what late reconciliation
            // observes (store tracker 6.29).
            let askPromise: ReturnType<typeof api.question.ask> | null = null;
            let timedOut = false;
            let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
            try {
                askPromise = api.question.ask(
                    {
                        question: text,
                        index: config.IndexName,
                        context: history,
                        sessionid: appState.sessionid,
                        current_url: appState.pageUrl || undefined,
                        // Hidden conditional fields stay out (registry-wide
                        // chain resolution, tracker 6.26b): a child revealed by
                        // this turn's own fills enters the list on the NEXT
                        // turn, mirroring the backend's per-turn resolution.
                        open_fields: toOpenFieldSpecs(turnForm ? openForAI(turnForm, current, registry) : []),
                        // Store tracker 6.64: the union of fields the visitor
                        // has explicitly confirmed in the form view (or via a
                        // suggestion confirm), so extraction never re-fills a
                        // field the visitor has locked in — this whole call
                        // only ever runs while `registry` is non-null (guarded
                        // at the top of `runTurn`), so a legacy/no-forms turn
                        // (which goes through `Utilities/SendMessage.ts`
                        // instead) never carries this field at all.
                        confirmed_field_ids: current.confirmedFieldIds,
                        // Platform contract (tracker 6.15): `form_objectid`
                        // names a form the student is *actively filling*, and
                        // the offer selector reads it as "do not offer this
                        // one". Sending it before anything is started told the
                        // server to exclude the only form this tenant has, so
                        // the form was never offered and the start card could
                        // not appear. Open fields still ride every turn — this
                        // marker is about what is in progress, not what may be
                        // extracted.
                        form_objectid: current.activeFormId ?? undefined,
                    },
                    config.CustomPrompt ?? false
                );
                result = await Promise.race([
                    askPromise,
                    new Promise<never>((_, reject) => {
                        timeoutTimer = setTimeout(() => {
                            timedOut = true;
                            reject(new Error('timeout'));
                        }, TURN_TIMEOUT_MS);
                    }),
                ]);
            } catch (_) {
                result = null;
            }

            clearTimeout(timeoutTimer);
            clearTimeout(slowTimer);
            dispatch({ type: 'setSlowTurn', slowTurn: false });

            const elapsed = Date.now() - startedAt;
            if (elapsed < MIN_THINKING_MS) await sleep(MIN_THINKING_MS - elapsed);

            // A turn resolves to one of three surfaces; see `classifyTurn`.
            const outcome = classifyTurn(result);
            if (!result || outcome === 'retryable') {
                // The user's message stays; only this turn resolves to the
                // bubble. The two retryable surfaces make different claims
                // (store tracker 6.29): a failed call or server-declared
                // retryable error may promise nothing was lost — that rides
                // the platform guarantee — but at a client timeout the backend
                // may still commit the turn, so that bubble promises nothing,
                // carries the turn's identity, and leaves the request running
                // for a late success to reconcile into it. Returning here
                // releases the FIFO chain either way, exactly as before.
                const turnId = timedOut && askPromise ? uuidv4() : undefined;
                addMessage({
                    content: turnId ? TURN_TIMEOUT_MESSAGE : TURN_ERROR_MESSAGE,
                    direction: 1,
                    time: new Date(Date.now()),
                    formError: true,
                    retryText: text,
                    turnId,
                });
                dispatch({ type: 'setThinking', thinking: false });
                if (turnId && askPromise) {
                    lateTurnIdsRef.current.add(turnId);
                    askPromise.then(
                        (late) => reconcileLateTurn(turnId, text, turnForm?.objectId, late),
                        // A late failure changes nothing: the bubble already
                        // says try again.
                        () => lateTurnIdsRef.current.delete(turnId)
                    );
                }
                return;
            }

            // F3: bound to the form captured above, before the await — the
            // form this turn was actually sent for, immune to a pause (or
            // any other pin change) that happened purely while it was
            // in flight.
            const receipts = commitFills(turnForm?.objectId, result.fills);
            // Engagement closure (store tracker 6.55): pin only from the
            // response's activation signal (an in-window offer acceptance) —
            // never from the fill above alone. `activate` itself no-ops if
            // the form is already active or unknown.
            if (
                result.form_activated &&
                registry?.byId.get(result.form_activated) &&
                stateRef.current.activeFormId !== result.form_activated
            ) {
                activate(result.form_activated, { activationTurn: false });
            }
            // A declined offer (a negative reply inside the offer window) is
            // quiet: the queue entry, if any, is removed; no data is touched.
            if (result.form_declined) dispatch({ type: 'removeQueued', formId: result.form_declined });
            routeQueued(result.form_queued ?? undefined);
            const cardOfferId = routeOffer(result.form_offer ?? undefined);

            addMessage({
                content: result.answer,
                direction: 1,
                time: new Date(Date.now()),
                relatedLinks: result.links,
                cards: result.cards,
                fills: receipts?.length ? receipts : undefined,
                formOfferId: cardOfferId,
                formCompleteId: result.form_complete ?? undefined,
                // 1.1 / bug cluster D: the answer above always renders — this
                // never masks it — but a retryable extraction error still
                // earns its own quiet retry affordance.
                fillRetryText: fillsRetryable(result) ? text : undefined,
                // The reply stands; the notice says the fill did not land, so a
                // student who volunteered details is never left assuming they
                // were recorded. When the turn carried fills too, receipts
                // render beside the notice and the copy narrows to "some"
                // (platform tracker 6.17).
                fillNotice:
                    outcome === 'unrecorded' ? (receipts?.length ? 'partial' : true) : undefined,
            });
            dispatch({ type: 'setThinking', thinking: false });
        },
        [registry, formFor, addMessage, dispatch, appState.messages, appState.sessionid, appState.pageUrl, config.IndexName, config.CustomPrompt, commitFills, routeOffer, routeQueued, reconcileLateTurn, activate]
    );

    const sendTurn = useCallback(
        async (text: string) => {
            const trimmed = text.trim();
            if (!trimmed || !active) return;
            // The user's bubble lands immediately; the turn itself may wait its turn.
            addMessage({ content: trimmed, direction: 0, time: new Date(Date.now()) });
            dispatch({ type: 'setDraft', draft: '' });
            await enqueueTurn(() => runTurn(trimmed));
        },
        [active, addMessage, dispatch, enqueueTurn, runTurn]
    );

    const retryTurn = useCallback(
        (index: number, text: string) => {
            appDispatch({ type: 'removeMessage', index });
            enqueueTurn(() => runTurn(text));
        },
        [appDispatch, enqueueTurn, runTurn]
    );

    /* ---------------------------------------------------------------
       3.2 — suggested mode
    ----------------------------------------------------------------*/
    // Confirming is the one moment the widget writes: in suggested mode the
    // backend returned the fills without persisting them, so the student's
    // confirm is what commits. The write carries `chatorigin` for exactly the
    // fields being confirmed here, which is what lets the backend emit
    // `FieldFilledViaChat` at the write — the widget never emits it itself, in
    // either mode.
    const confirmSuggestion = useCallback(
        (fieldId: string) => {
            const value = stateRef.current.suggested[fieldId];
            if (value === undefined || !form) return;
            dispatch({ type: 'confirmSuggestion', fieldId });
            persist({ ...stateRef.current.values, [fieldId]: value }, new Set([fieldId]));
        },
        [dispatch, persist, form]
    );

    const confirmAllSuggestions = useCallback(() => {
        const pending = stateRef.current.suggested;
        const confirmedIds = Object.keys(pending);
        if (!confirmedIds.length || !form) return;
        dispatch({ type: 'confirmAllSuggestions' });
        persist({ ...stateRef.current.values, ...pending }, new Set(confirmedIds));
    }, [dispatch, persist, form]);

    const undoSuggestion = useCallback(
        (fieldId: string) => dispatch({ type: 'undoSuggestion', fieldId }),
        [dispatch]
    );

    /* ---------------------------------------------------------------
       3.3 — discard
    ----------------------------------------------------------------*/
    const requestDiscard = useCallback(
        () => dispatch({ type: 'setConfirmingDiscard', confirming: true }),
        [dispatch]
    );
    const keepForm = useCallback(
        () => dispatch({ type: 'setConfirmingDiscard', confirming: false }),
        [dispatch]
    );

    const discardForm = useCallback(() => {
        const target = formFor(stateRef.current);
        if (!target) return;
        // Multi-form handoff (delta spec): with a queued form waiting, the
        // discard acknowledgement names the queue head. A never-started head
        // rides it as a fresh offer — the reducer pops it, so the start card is
        // its one live surface. A STARTED head stays queued instead (store
        // tracker 6.28): the start card suppresses started forms, so an offer
        // card would be dead and popping the row would strand the form; the
        // acknowledgement still names it and its Up next row is the way in.
        const nextId = stateRef.current.queuedForms[0];
        const next = nextId ? registry?.byId.get(nextId) : undefined;
        const nextStarted = nextId ? stateRef.current.startedFormIds.includes(nextId) : false;
        // Local discard first and unconditionally: a failed dismiss degrades
        // suppression, never the student's ability to clear the form.
        const discardedFieldIds = allFieldIds(target);
        dispatch({ type: 'discardForm', fieldIds: discardedFieldIds });
        // The discarded form's values are gone; its help threads were about
        // filling them, so they go too rather than outliving the fields they
        // discussed (unlike a plain sheet close, which keeps history — design
        // D3 is about the session, not about a form that no longer exists).
        updateHelpThreads((prev) => {
            if (!discardedFieldIds.some((id) => id in prev)) return prev;
            const remaining = { ...prev };
            discardedFieldIds.forEach((id) => delete remaining[id]);
            return remaining;
        });
        setHelpFieldId((current) => (current && discardedFieldIds.includes(current) ? null : current));
        addMessage(
            next
                ? {
                      content: discardWithQueueAcknowledgement(next.short),
                      direction: 1,
                      time: new Date(Date.now()),
                      formOfferId: nextStarted ? undefined : next.objectId,
                  }
                : { content: DISCARD_ACKNOWLEDGEMENT, direction: 1, time: new Date(Date.now()) }
        );
        // `FormDiscarded` is emitted by the dismiss endpoint, at the write —
        // logging it here too would double-count the discard.
        api.forms
            .dismiss({
                sessionid: appState.sessionid,
                index: config.IndexName ?? '',
                objectid: target.objectId,
                kind: 'explicit',
                pageurl: appState.pageUrl || null,
            })
            .catch(() => undefined);
    }, [formFor, registry, dispatch, addMessage, config.IndexName, appState.sessionid, appState.pageUrl, updateHelpThreads]);

    /* ---------------------------------------------------------------
       Submission (form-view slice) — the same full-list post the confirm
       path uses, surfaced so the overlay can drive its sending / failed
       states and the success overlay. Failure keeps everything local. No
       `chatorigin` marks: a clean submit confirms no pending suggestion
       (validation blocks while any are pending), and re-marking fields an
       earlier confirm already attributed would double-count the fill.
    ----------------------------------------------------------------*/
    const submitForm = useCallback(
        (): Promise<boolean> => persist(stateRef.current.values),
        [persist]
    );

    const completeSubmission = useCallback(() => {
        const target = formFor(stateRef.current);
        if (!target) return;
        // Post-submit handoff (delta spec): Done offers the next queued form.
        // A never-started head gets its start card and leaves the queue; a
        // STARTED head keeps its queue row — the start card suppresses started
        // forms, so a card could not carry it (store tracker 6.28) — while the
        // acknowledgement still names it either way.
        const nextId = stateRef.current.queuedForms[0];
        const next = nextId ? registry?.byId.get(nextId) : undefined;
        const nextStarted = nextId ? stateRef.current.startedFormIds.includes(nextId) : false;
        dispatch({ type: 'markSubmitted', formId: target.objectId });
        // `FormSubmitted` is emitted by the backend at the write, never here.
        addMessage(
            next
                ? {
                      content: submittedNextUpAcknowledgement(next.short),
                      direction: 1,
                      time: new Date(Date.now()),
                      formOfferId: nextStarted ? undefined : next.objectId,
                  }
                : { content: SUBMITTED_ACKNOWLEDGEMENT, direction: 1, time: new Date(Date.now()) }
        );
    }, [formFor, registry, dispatch, addMessage]);

    /* ---------------------------------------------------------------
       Forms-corrections slice — the update re-post (design D3). Reuses
       `persist` wholesale (same validate-elsewhere, sending, failure-callout,
       retry contract as first submit); the only divergence is at success,
       handled by `completeUpdate` below rather than `completeSubmission`.
    ----------------------------------------------------------------*/
    const updateSubmission = useCallback(
        (formId: string): Promise<boolean> => {
            // Snapshot at call time — the review overlay leaves fields
            // editable through the ~1-2s POST, so a correction can land after
            // this snapshot is taken and before the write resolves. `persist`
            // sends exactly this snapshot; nothing later ever gets
            // transmitted by *this* write. Compared again in `completeUpdate`
            // below to decide whether the dirty flag that would have caught
            // that late edit is safe to clear (fix: mid-flight correction
            // silently lost — the write "succeeding" must not also erase the
            // one signal that a further, unsent edit exists).
            const snapshot = stateRef.current.values;
            snapshotRef.current.set(formId, snapshot);
            return persist(snapshot, undefined, formId);
        },
        [persist]
    );

    /**
     * Success path for an update (D3/D5): clears dirty (unless a field
     * changed between the snapshot `updateSubmission` sent and now — that
     * edit was never transmitted by this write, so dirty must survive to
     * catch it on the next update) and posts a light chat acknowledgement —
     * deliberately not `completeSubmission`. The form was already submitted,
     * so there is no queue handoff to run and no second `markSubmitted` to
     * dispatch (that would double the id in `submittedFormIds` were it not
     * already idempotent, and more to the point would replay the post-submit
     * handoff for a form that never left `submittedFormIds`). `FormUpdated`
     * is the backend's single-emitter event at the write, never emitted here.
     */
    const completeUpdate = useCallback(
        (formId: string) => {
            const target = registry?.byId.get(formId);
            if (!target) return;
            const snapshot = snapshotRef.current.get(formId);
            snapshotRef.current.delete(formId);
            const current = stateRef.current.values;
            const changedMidFlight =
                snapshot !== undefined &&
                target.fields.some((f) => !sameValue(snapshot[f.id], current[f.id]));
            if (!changedMidFlight) {
                dispatch({ type: 'clearDirty', formId });
            }
            addMessage({
                content: updateAcknowledgement(target.short),
                direction: 1,
                time: new Date(Date.now()),
            });
        },
        [registry, dispatch, addMessage]
    );

    const value = useMemo<FormsContextValue>(
        () => ({
            active,
            form,
            registry,
            state,
            dispatch,
            theme,
            pct: progressPct(form, state, registry),
            pendingCount: suggestedCount(form, state),
            nextRequiredField: nextRequired(form, state, registry),
            sendTurn,
            retryTurn,
            startForm,
            switchToForm,
            pauseForm,
            removeQueuedForm,
            confirmSuggestion,
            confirmAllSuggestions,
            undoSuggestion,
            requestDiscard,
            keepForm,
            discardForm,
            formViewOpen,
            openFormView,
            closeFormView,
            submitForm,
            completeSubmission,
            reviewFormId,
            openReview,
            updateSubmission,
            completeUpdate,
            flipRectRef,
            helpFieldId,
            helpThreads,
            openHelp,
            closeHelp,
            sendHelp,
            retryHelp,
            helpBusyFieldIds,
            helpSlowFieldIds,
        }),
        [active, form, registry, state, dispatch, theme, sendTurn, retryTurn, startForm, switchToForm, pauseForm, removeQueuedForm, confirmSuggestion, confirmAllSuggestions, undoSuggestion, requestDiscard, keepForm, discardForm, formViewOpen, openFormView, closeFormView, submitForm, completeSubmission, reviewFormId, openReview, updateSubmission, completeUpdate, helpFieldId, helpThreads, openHelp, closeHelp, sendHelp, retryHelp, helpBusyFieldIds, helpSlowFieldIds]
    );

    return <FormsContext.Provider value={value}>{children}</FormsContext.Provider>;
};
